import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import AppContext from '@/contexts/appContext';
import useAPI from '@/hooks/useAPI';
import usePagination from '@/hooks/usePagination';
import {
  BulkActionBar,
  BulkMessageModal,
  myPeopleDefaultSearchParams
} from '@/pages/MyPeople/components';
import PersonRepository from '@/repositories/PersonRepository';
import { DIRECTION } from '@/types/sort';
import Alert from '@/ui/components/Alert';
import Heading from '@/ui/components/Heading';
import Paginator from '@/ui/components/Paginator';
import Spinner from '@/ui/components/Spinner';

import { MyPeopleFilterForm, PersonRow } from './components';

const MyPeople = () => {
  const { addMessage, currentUser } = useContext(AppContext);
  const personRepository = PersonRepository();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const handleClearSelection = useCallback(() => setSelectedIds([]), []);

  const [searchParams, setSearchParams] = useSearchParams();
  const activeParams = {
    direction: searchParams.get('direction') ?? myPeopleDefaultSearchParams.direction,
    ordering: searchParams.get('ordering') ?? myPeopleDefaultSearchParams.ordering,
    page: searchParams.get('page') ?? myPeopleDefaultSearchParams.page,
    search: searchParams.get('search') ?? myPeopleDefaultSearchParams.search
  };

  const [totalPages, setTotalPages] = useState<number>(0);

  const { data, isError, isPending, isSuccess } = useAPI({
    queryKey: ['getMyPeople', currentUser?.id, activeParams],
    queryFn: async () => {
      const { direction, ordering, page, search } = activeParams;
      return await personRepository.getMyPeople({
        ordering: `${direction === DIRECTION.ASC ? '' : '-'}${ordering}`,
        page,
        ...(search && { search })
      });
    },
    enabled: !!currentUser?.id
  });

  useEffect(() => {
    if (isSuccess && data.data?.total_pages) {
      setTotalPages(data.data.total_pages);
    }
  }, [addMessage, data?.data?.total_pages, isSuccess]);

  const selectedPeople = useMemo(
    () => (data?.data?.results ?? []).filter((person) => selectedIds.includes(person.id)),
    [data, selectedIds]
  );

  const { currentPage, goToPage, nextPage, prevPage } = usePagination(
    +activeParams.page,
    totalPages
  );

  useEffect(() => {
    if (currentPage !== +myPeopleDefaultSearchParams.page)
      setSearchParams((currentParams) => {
        currentParams.set('page', currentPage.toString());
        return currentParams;
      });
    else
      setSearchParams((currentParams) => {
        currentParams.delete('page');
        return currentParams;
      });
    // setSearchParams dependency was causing infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  if (!currentUser?.id)
    return (
      <Alert level="error">
        Unable to identify the current Dugout user to get the assigned people
      </Alert>
    );

  return (
    <>
      <MyPeopleFilterForm activeParams={activeParams} setParams={setSearchParams} />

      {isPending ? (
        <div className="text-center w-full">
          <Spinner />
        </div>
      ) : !isError && data.data?.results?.length ? (
        <ul className="mt-2">
          {data.data.results.map((person) => (
            <li key={person.id}>
              <PersonRow
                person={person}
                isSelected={selectedIds.includes(person.id)}
                onToggleSelect={handleToggleSelect}
              />
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-center p-8">
          <Heading
            text="You don't have assigned people (or your search didn't return results)"
            value={3}
          />
        </div>
      )}

      <Paginator
        currentPage={currentPage}
        goToPage={goToPage}
        nextPage={nextPage}
        prevPage={prevPage}
        totalPages={totalPages}
      />

      <BulkActionBar
        selectedCount={selectedIds.length}
        onSendMessage={() => setIsBulkModalOpen(true)}
        onClear={handleClearSelection}
      />

      <BulkMessageModal
        open={isBulkModalOpen}
        onClose={() => setIsBulkModalOpen(false)}
        selectedPeople={selectedPeople}
        onSuccess={handleClearSelection}
      />
    </>
  );
};

export default MyPeople;
